import React, { useState, useEffect, useCallback } from 'react';
import { useResume } from './context/ResumeContext';
import { useAutosave } from './context/AutosaveContext';
import { useHistory } from './context/HistoryContext';
import SectionRenderer from './sections/SectionRenderer';
import { ResumeSchemaService } from '../../core/schema/ResumeSchemaService';
import CustomizeResumeModal from './components/CustomizeResumeModal';
import { useWorkspaceContext } from '../../pages/ChatPage/providers/WorkspaceProvider';
import { useAppContext } from '../../context/AppContext';

export default function ResumeEditor() {
  const { activeResume, completionPercentage, updateResumeState } = useResume();
  const { saveStatus, lastSaved } = useAutosave();
  const { undo, redo, canUndo, canRedo } = useHistory();
  const { theme = 'light', setActiveTab } = useWorkspaceContext() || {};
  const { handleAIBoost } = useAppContext();

  const [expandedSectionId, setExpandedSectionId] = useState('contact');
  const [relativeTimeLabel, setRelativeTimeLabel] = useState('');
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);

  const handleUndoClick = useCallback(() => {
    if (!canUndo) return;
    const previous = undo(JSON.parse(JSON.stringify(activeResume)));
    updateResumeState(previous, false); // false = don't push to history again
  }, [canUndo, undo, activeResume, updateResumeState]);

  const handleRedoClick = useCallback(() => {
    if (!canRedo) return;
    const next = redo(JSON.parse(JSON.stringify(activeResume)));
    updateResumeState(next, false);
  }, [canRedo, redo, activeResume, updateResumeState]);

  // Keyboard shortcuts (Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (isCmdOrCtrl) {
        if (e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            handleRedoClick();
          } else {
            handleUndoClick();
          }
        } else if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          handleRedoClick();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndoClick, handleRedoClick]);

  // Relative lastSaved display
  useEffect(() => {
    if (!lastSaved) {
      setRelativeTimeLabel('');
      return;
    }

    const updateLabel = () => {
      const diffMs = new Date() - lastSaved;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);

      if (diffSec < 5) {
        setRelativeTimeLabel('just now');
      } else if (diffSec < 60) {
        setRelativeTimeLabel('just now');
      } else if (diffMin === 1) {
        setRelativeTimeLabel('1 minute ago');
      } else {
        setRelativeTimeLabel(`${diffMin} minutes ago`);
      }
    };

    updateLabel();
    const interval = setInterval(updateLabel, 10000);
    return () => clearInterval(interval);
  }, [lastSaved]);

  const getStatusIndicator = () => {
    switch (saveStatus) {
      case 'dirty':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--status-warning)' }} />
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Unsaved Changes</span>
          </div>
        );
      case 'saving':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span className="w-2 h-2 rounded-full border-2 border-brand border-t-transparent animate-spin" style={{ width: '8px', height: '8px', display: 'inline-block' }} />
            <span style={{ fontSize: '11px', color: 'var(--brand)' }}>Saving...</span>
          </div>
        );
      case 'saved':
      case 'idle':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--status-success)' }} />
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              Saved {relativeTimeLabel ? relativeTimeLabel : ''}
            </span>
          </div>
        );
      case 'error':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--status-danger)' }} />
            <span style={{ fontSize: '11px', color: 'var(--status-danger)', fontWeight: 600 }}>Save Failed</span>
          </div>
        );
      default:
        return null;
    }
  };

  const orderedSections = ResumeSchemaService.getOrderedSections(activeResume);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Top Header Panel (Autosave + Undo/Redo Controls) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: '1px solid var(--divider)',
          background: 'var(--surface-0)',
          flexShrink: 0,
        }}
      >
        {getStatusIndicator()}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={handleUndoClick}
            disabled={!canUndo}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid var(--card-border)',
              background: 'var(--surface-1)',
              color: canUndo ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '11.5px',
              fontWeight: 600,
              cursor: canUndo ? 'pointer' : 'not-allowed',
              opacity: canUndo ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>↩</span> Undo
          </button>
          <button
            onClick={handleRedoClick}
            disabled={!canRedo}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid var(--card-border)',
              background: 'var(--surface-1)',
              color: canRedo ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '11.5px',
              fontWeight: 600,
              cursor: canRedo ? 'pointer' : 'not-allowed',
              opacity: canRedo ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            Redo <span>↪</span>
          </button>
        </div>
      </div>

      {/* Progress Bar Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--divider)',
          background: 'var(--surface-0)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: '11.5px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Resume Completion
          </span>
          <span style={{ fontSize: '12px', fontWeight: 800, color: 'var(--brand)' }}>
            {completionPercentage}%
          </span>
        </div>
        <div style={{ width: '100%', height: '6px', background: 'var(--surface-2)', borderRadius: '3px', overflow: 'hidden' }}>
          <div
            style={{
              width: `${completionPercentage}%`,
              height: '100%',
              background: 'var(--brand)',
              borderRadius: '3px',
              transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </div>
      </div>

      {/* Scrollable Sections List Accordions */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {orderedSections.map((sec, idx) => (
          <SectionRenderer
            key={sec.id}
            section={sec}
            isExpanded={expandedSectionId === sec.id}
            onToggle={() => setExpandedSectionId(expandedSectionId === sec.id ? null : sec.id)}
            canMoveUp={idx > 0}
            canMoveDown={idx < orderedSections.length - 1}
          />
        ))}

        {/* Customize Resume Button */}
        <button
          onClick={() => setIsCustomizeModalOpen(true)}
          style={{
            marginTop: 8,
            padding: '12px',
            background: 'var(--surface-1)',
            border: '1px dashed var(--border-default)',
            color: 'var(--brand)',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            boxSizing: 'border-box',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-light)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-1)'}
        >
          <span>⚙</span> Customize Resume
        </button>

        {/* AI Action Card */}
        <div style={{
          marginTop: 8,
          padding: '16px',
          background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(79,70,229,0.06) 100%)',
          border: '1px solid rgba(124,58,237,0.2)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          boxShadow: '0 4px 12px rgba(124,58,237,0.03)'
        }}>
          <div>
            <h4 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '14px' }}>✨</span> AI Optimize & Build
            </h4>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '11px', lineHeight: 1.4 }}>
              Instantly optimize your resume against ATS rules or chat with the AI helper to build custom sections.
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleAIBoost}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.15s ease',
                boxShadow: '0 2px 8px rgba(124,58,237,0.2)'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 0.9}
              onMouseLeave={e => e.currentTarget.style.opacity = 1}
            >
              🚀 AI Boost
            </button>
            
            <button
              onClick={() => {
                if (typeof setActiveTab === 'function') {
                  setActiveTab('chat');
                }
              }}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'var(--surface-1)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-1)'}
            >
              🤖 AI Builder
            </button>
          </div>
        </div>
      </div>

      {/* Customize Sections Modal */}
      <CustomizeResumeModal
        isOpen={isCustomizeModalOpen}
        onClose={() => setIsCustomizeModalOpen(false)}
        resume={activeResume}
        onEnableSection={(id) => {
          const next = ResumeSchemaService.enableSection(activeResume, id);
          updateResumeState(next);
          setExpandedSectionId(id); // Expand the newly added section!
        }}
        onDisableSection={(id) => {
          const next = ResumeSchemaService.disableSection(activeResume, id);
          updateResumeState(next);
        }}
        theme={theme}
      />
    </div>
  );
}
